import { useQuery } from '@tanstack/react-query';
import { FilterTag, FilterDropdown } from 'modules/shared-components/filters';
import { useMemo, useState } from 'react';
import {
  ExchangeService,
  exchangeKeys,
} from 'api/services/httpServices/ExchangeService';
import {
  ExchangeIconStretch,
  ExchangeIcon,
} from 'modules/shared-components/exchange/exchange-icon';
import { duration } from 'moment';

type ExchangeTagProps = { value: number } & Omit<
  React.ComponentProps<typeof FilterTag>,
  'label'
>;

export function ExchangeFilterTag({ value, ...rest }: ExchangeTagProps) {
  const { data: exchanges } = useQuery({
    queryFn: ExchangeService.getExchanges,
    queryKey: exchangeKeys.getExchanges(),
    staleTime: duration(1, 'hour').asMilliseconds(),
  });

  const selectedExchange = exchanges?.find((exchange) => exchange.id === value);

  return selectedExchange ? (
    <FilterTag
      {...rest}
      icon={<ExchangeIconStretch imgName={selectedExchange.key} />}
      label={selectedExchange.name}
    />
  ) : null;
}

type ExchangeFilterProps = {
  value: number[];
  onChange: (value: number[]) => void;
};

export function ExchangeFilter({ value = [], onChange }: ExchangeFilterProps) {
  const [isEnabled, setIsEnabled] = useState(false);

  const {
    data: exchanges,
    isError,
    isLoading,
  } = useQuery({
    queryFn: ExchangeService.getExchanges,
    queryKey: exchangeKeys.getExchanges(),
    staleTime: duration(1, 'hour').asMilliseconds(),
    enabled: isEnabled,
  });

  const options = useMemo(() => {
    return exchanges?.map((exchange) => ({
      label: exchange.name,
      value: exchange.id,
      icon: <ExchangeIcon imgName={exchange.key} />,
    }));
  }, [exchanges]);

  return (
    <FilterDropdown
      value={value}
      options={options}
      label="Exchange"
      onChange={onChange}
      isError={isError}
      onOpen={() => setIsEnabled(true)}
      loading={isLoading}
    />
  );
}
